import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/stripe/status/${sessionId}`);
      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  if (status === 'loading') {
    return (
      <div className="pt-24 md:pt-28 min-h-screen container-custom" data-testid="checkout-success-loading">
        <div className="text-center py-20">
          <div className="animate-spin w-12 h-12 border-2 border-white border-t-transparent rounded-full mx-auto mb-6" />
          <h1 className="font-['Syne'] text-2xl font-bold mb-4">Processing your order...</h1>
          <p className="text-muted-foreground">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="pt-24 md:pt-28 min-h-screen container-custom" data-testid="checkout-success-page">
        <div className="text-center py-20 max-w-lg mx-auto">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
          <h1 className="font-['Syne'] text-3xl font-bold mb-4">Order Confirmed!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you for your purchase. You will receive a confirmation email shortly.
          </p>
          
          {paymentInfo && (
            <div className="border border-neutral-800 p-6 mb-8 text-left">
              <h3 className="font-bold mb-4">Order Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span>{formatPrice(paymentInfo.amount_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="text-green-500 capitalize">{paymentInfo.payment_status}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button className="btn-primary rounded-none" data-testid="continue-shopping-btn">
                Continue Shopping
              </Button>
            </Link>
            <Link to="/dashboard?tab=orders">
              <Button variant="outline" className="btn-outline rounded-none" data-testid="view-orders-btn">
                View Orders
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-28 min-h-screen container-custom" data-testid="checkout-error">
      <div className="text-center py-20">
        <h1 className="font-['Syne'] text-2xl font-bold mb-4">
          {status === 'timeout' ? 'Verification Timeout' : 'Something went wrong'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {status === 'timeout'
            ? 'We could not verify your payment. Please check your email for confirmation.'
            : 'There was an issue processing your payment.'}
        </p>
        <Link to="/shop">
          <Button className="btn-primary rounded-none">Back to Shop</Button>
        </Link>
      </div>
    </div>
  );
}
